import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search,
  Visibility,
  LocalShipping,
  Inventory,
  VerifiedUser,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { DataService } from '../../services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import Loader from 'src/components/Loader';
import {
  TransferOrderStatus,
  getTransferOrderStatusLabel,
  getTransferOrderStatusColor,
} from 'src/types/TransferOrder.types';

async function readData<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
  }
  const body = await res.json().catch(() => null);

  if (body && typeof body === "object" && "data" in body) {
    if ("success" in body && body.success === false) {
      const msg = typeof body.message === "string" && body.message.trim()
        ? body.message
        : "Request failed";
      throw new Error(msg);
    }
    return body.data as T;
  }
  return body as T;
}

interface TransferOrderListItem {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  status: TransferOrderStatus;
  itemCount: number;
  totalRequestedQuantity: number;
  totalAllocatedQuantity: number;
  totalPickedQuantity: number;
  createdAt: string;
  updatedAt: string;
}

const OrderReceivingList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [orders, setOrders] = useState<TransferOrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TransferOrderStatus | 'all'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      let url = 'api/transferorder';
      const params: string[] = [];

      if (statusFilter !== 'all') {
        params.push(`status=${statusFilter}`);
      }

      if (params.length > 0) {
        url += '?' + params.join('&');
      }

      const response = await DataService.get(url);
      if (!response.ok) {
        throw new Error('Failed to load transfer orders');
      }

      const data = await readData<TransferOrderListItem[]>(response);

      const receivingOrders = data.filter(order =>
        order.status === TransferOrderStatus.InTransit ||
        order.status === TransferOrderStatus.Received ||
        order.status === TransferOrderStatus.Validating ||
        order.status === TransferOrderStatus.Completed
      );

      setOrders(receivingOrders);
    } catch (err: any) {
      dispatch(
        showAlert({
          message: err.message || 'Failed to load orders',
          type: 'error'
        })
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dispatch]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    return (
      order.name.toLowerCase().includes(searchLower) ||
      order.clientName.toLowerCase().includes(searchLower) ||
      order.id.toLowerCase().includes(searchLower)
    );
  });

  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewOrder = (orderId: string) => {
    navigate(`/order-receiving/${orderId}`);
  };

  const getStatusIcon = (status: TransferOrderStatus) => {
    switch (status) {
      case TransferOrderStatus.InTransit:
        return <LocalShipping />;
      case TransferOrderStatus.Received:
        return <Inventory />;
      case TransferOrderStatus.Validating:
        return <VerifiedUser />;
      case TransferOrderStatus.Completed:
        return <CheckCircle />;
      default:
        return <Schedule />;
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 3 }}>
      <Container maxWidth="xl">
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <Typography variant="h4" fontWeight="600">
                  Order Receiving
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Receive and validate incoming transfer orders
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search by order name, client, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value as TransferOrderStatus | 'all')}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value={TransferOrderStatus.InTransit}>In Transit</MenuItem>
                    <MenuItem value={TransferOrderStatus.Received}>Received</MenuItem>
                    <MenuItem value={TransferOrderStatus.Validating}>Validating</MenuItem>
                    <MenuItem value={TransferOrderStatus.Completed}>Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocalShipping color="warning" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      In Transit
                    </Typography>
                    <Typography variant="h5" fontWeight="600">
                      {orders.filter(o => o.status === TransferOrderStatus.InTransit).length}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Inventory color="info" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Received
                    </Typography>
                    <Typography variant="h5" fontWeight="600">
                      {orders.filter(o => o.status === TransferOrderStatus.Received).length}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <VerifiedUser color="primary" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Validating
                    </Typography>
                    <Typography variant="h5" fontWeight="600">
                      {orders.filter(o => o.status === TransferOrderStatus.Validating).length}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircle color="success" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Completed
                    </Typography>
                    <Typography variant="h5" fontWeight="600">
                      {orders.filter(o => o.status === TransferOrderStatus.Completed).length}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order Name</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell align="center">Items</TableCell>
                  <TableCell align="center">Total Quantity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedOrders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {order.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.clientName}</TableCell>
                      <TableCell align="center">{order.itemCount}</TableCell>
                      <TableCell align="center">{order.totalRequestedQuantity}</TableCell>
                      <TableCell>
                        <Chip
                          label={getTransferOrderStatusLabel(order.status)}
                          color={getTransferOrderStatusColor(order.status)}
                          icon={getStatusIcon(order.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(order.createdAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(order.updatedAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(order.updatedAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Order">
                          <IconButton
                            color="primary"
                            onClick={() => handleViewOrder(order.id)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredOrders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Box>
  );
};

export default OrderReceivingList;